import Bugsnag from '@bugsnag/js';
import BugsnagPluginExpress from '@bugsnag/plugin-express';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import getConfig from 'next/config';

interface IConfig {
  apiKey: string;
  appType: string;
  appVersion: string;
  enabledReleaseStages?: string[];
  plugins: BugsnagPluginReact[];
  releaseStage: string;
}

export const getBugsnagApiKey = (): string => {
  const { publicRuntimeConfig, serverRuntimeConfig } = getConfig();
  const isServer = typeof window === 'undefined';

  const apiKey = isServer
    ? serverRuntimeConfig.BUGSNAG_SERVER_API_KEY
    : publicRuntimeConfig.BUGSNAG_BROWSER_API_KEY;

  return apiKey;
};

export const startBugsnag = (): boolean => {
  const { publicRuntimeConfig } = getConfig();
  const apiKey = getBugsnagApiKey();

  if (!apiKey) return false;

  const isServer = typeof window === 'undefined';
  const appType = isServer ? 'server' : 'browser';

  const plugins = [new BugsnagPluginReact(), ...(isServer ? [BugsnagPluginExpress] : [])];

  const config: IConfig = {
    apiKey,
    appType,
    appVersion: publicRuntimeConfig.APP_VERSION,
    enabledReleaseStages: ['staging', 'production'],
    plugins,
    releaseStage: publicRuntimeConfig.RELEASE_ENV,
  };

  // Fix the console warning about bugsnag being started more than once
  if (!Bugsnag?.isStarted()) {
    Bugsnag.start(config);
  }

  return true;
};

// Copied from: https://github.com/bugsnag/bugsnag-js/blob/next/packages/plugin-react/src/index.js
export const formatComponentStack = (str) => {
  const lines: string[] = str.split(/\s*\n\s*/g);
  let ret = '';
  for (let line = 0, len = lines.length; line < len; line += 1) {
    if (lines[line].length > 0) ret += `${ret.length > 0 ? '\n' : ''}${lines[line]}`;
  }
  return ret;
};

/**
 * [notifyReactError]
 * This method is called inside of our own <ErrorBoundary>, so that are still reported
 * to Bugsnag rather than swallowed up. If report generation fails, fall back to the standard notify() method.
 *
 * Modified from Bugsnag's own ErrorBoundary implementation.
 *   https://github.com/bugsnag/bugsnag-js/blob/next/packages/plugin-react/src/index.js
 */
export const notifyReactError = (error, info) => {
  try {
    const handledState = {
      severity: 'error',
      unhandled: true,
      severityReason: { type: 'unhandledException' },
    };

    const event = Bugsnag.Event.create(error, true, handledState, info.componentStack, 0);

    if (info && info.componentStack) {
      /* eslint-disable-next-line no-param-reassign */
      info.componentStack = formatComponentStack(info.componentStack);
    }

    event.addMetadata('react', info);

    Bugsnag.notify(event?.originalError || new Error('ErrorBoundaryError'));

    // If the above fails
  } catch (ClientReportingError) {
    // Send error using vanilla method
    Bugsnag.notify(error);

    // Also trigger a warning, so we can diagnose our React integration
    Bugsnag.notify(ClientReportingError, (report) => {
      /* eslint-disable-next-line no-param-reassign */
      report.severity = 'warning';
    });
  }
};
